import { HOST } from './anchors';

export const ROOM_ATTR = 'data-tour-room';

export const TAIL_ATTR = 'data-tour-room-tail';

export const GUIDE_ATTR = 'data-tour-guide';

export const TAIL_VAR = '--tour-room-tail';

const EDGE = 14;

export const GUIDE_FALLBACK_HEIGHT = 260;

const MAX_TAIL = 520;

const EPSILON = 2;

function viewportHeight(): number {
  return window.innerHeight || document.documentElement.clientHeight || 0;
}

function viewportWidth(): number {
  return window.innerWidth || document.documentElement.clientWidth || 0;
}

export interface GuideMode {
  mode: 'beside' | 'sheet';
  side?: 'left' | 'right';
}

const CARD_WIDTH = 340;

const GAP = 14;

const CARD_MIN_BESIDE = 280;

const CLIP_INSET = 8;

export interface Band {
  top: number;
  bottom: number;
}

export function visibleBand(el: HTMLElement): Band {
  const viewport = viewportHeight();
  let top = 0;
  let bottom = viewport;

  for (let node = el.parentElement; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (!/auto|scroll|overlay/.test(`${style.overflowY}`)) continue;
    const box = node.getBoundingClientRect();
    if (box.width === 0 && box.height === 0) continue;
    top = Math.max(top, box.top + CLIP_INSET);
    bottom = Math.min(bottom, box.bottom - CLIP_INSET);
  }

  if (bottom - top < EPSILON) return { top: 0, bottom: viewport };
  return { top, bottom };
}

function wholeViewport(viewport: number): Band {
  return { top: 0, bottom: viewport };
}

export function guideMode(target: { left: number; right: number }): GuideMode {
  const width = viewportWidth();
  const card = Math.min(CARD_WIDTH, Math.max(0, width - EDGE * 2));
  if (card < CARD_MIN_BESIDE) return { mode: 'sheet' };

  const roomRight = width - target.right - GAP - EDGE;
  const roomLeft = target.left - GAP - EDGE;

  if (roomRight >= card) return { mode: 'beside', side: 'right' };
  if (roomLeft >= card) return { mode: 'beside', side: 'left' };
  return { mode: 'sheet' };
}

export function dressGuide(card: HTMLElement | null, placement: GuideMode): void {
  card?.setAttribute(GUIDE_ATTR, placement.mode);
}

export const COVERED_ATTR = 'data-tour-covered';

export interface Guide {
  height: number;
  sheet: boolean;
}

export function groupFits(height: number, guide: Guide, band?: Band): boolean {
  const viewport = viewportHeight();
  const top = Math.max(EDGE, band?.top ?? 0);
  const forGuide = guide.sheet ? viewport - guide.height - GAP - EDGE : viewport - EDGE;
  const bottom = Math.min(band?.bottom ?? viewport, forGuide);
  return height > 0 && height <= bottom - top;
}

export function liveGuide(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.driver-popover');
}

export function guideHeight(): number {
  const measured = liveGuide()?.getBoundingClientRect().height ?? 0;
  return measured > 0 ? measured : GUIDE_FALLBACK_HEIGHT;
}

export function fitTop(
  anchor: { top: number; height: number },
  guide: Guide,
  viewport: number,
  band: Band = wholeViewport(viewport),
): number {
  const bandTop = Math.max(EDGE, band.top);
  const bandBottom = Math.min(band.bottom, viewport - EDGE);

  const lowestForGuide = guide.sheet
    ? viewport - guide.height - GAP
    : viewport - EDGE - Math.min(guide.height, Math.max(0, viewport - EDGE * 2));
  const lowestForAnchor = bandBottom - anchor.height;

  const lowest = guide.sheet
    ? Math.min(lowestForGuide - anchor.height, lowestForAnchor)
    : Math.min(lowestForGuide, lowestForAnchor);

  return Math.min(Math.max(anchor.top, bandTop), Math.max(bandTop, lowest));
}

function isScrollable(el: HTMLElement, mayGrow: boolean): boolean {
  const style = window.getComputedStyle(el);
  if (!/auto|scroll|overlay/.test(`${style.overflowY}`)) return false;
  return mayGrow || el.scrollHeight - el.clientHeight > EPSILON;
}

function spaceNeighbours(el: HTMLElement): void {
  el.closest<HTMLElement>(HOST.sidebarMenuItem)?.setAttribute(ROOM_ATTR, '');
}

function scrollContainer(el: HTMLElement, amount: number, mayGrow: boolean): number {
  const before = el.scrollTop;
  el.scrollTop = before + amount;
  const moved = el.scrollTop - before;

  const short = amount - moved;
  if (!mayGrow || short <= EPSILON) return moved;

  const base = Number.parseFloat(window.getComputedStyle(el).paddingBottom) || 0;
  const tail = Math.min(MAX_TAIL, Math.ceil(short));
  el.style.setProperty(TAIL_VAR, `${base + tail}px`);
  el.setAttribute(TAIL_ATTR, '');

  const grown = el.scrollTop;
  el.scrollTop = grown + short;
  return moved + (el.scrollTop - grown);
}

function shiftIntoBand(el: HTMLElement, shift: number): void {
  let remaining = shift;

  for (let node = el.parentElement; node && Math.abs(remaining) > EPSILON; node = node.parentElement) {
    if (!isScrollable(node, shift > 0)) continue;
    remaining -= scrollContainer(node, remaining, shift > 0);
  }

  if (Math.abs(remaining) > EPSILON) {
    const before = window.scrollY;
    try {
      window.scrollBy({ top: remaining, behavior: 'auto' });
    } catch {
      window.scrollBy?.(0, remaining);
    }
    remaining -= window.scrollY - before;
  }
}

export function makeRoomFor(el: HTMLElement, guide: Guide): void {
  releaseRoom();

  spaceNeighbours(el);

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;

  const shift = rect.top - fitTop(rect, guide, viewportHeight(), visibleBand(el));
  if (Math.abs(shift) <= EPSILON) return;

  shiftIntoBand(el, shift);
}

export function keepTrackerClear(): void {
  const tracker = document.querySelector<HTMLElement>('.tour-guide-tracker');
  if (!tracker) return;

  const guide = liveGuide();
  if (!guide) {
    tracker.removeAttribute(COVERED_ATTR);
    return;
  }

  const card = guide.getBoundingClientRect();
  const panel = tracker.getBoundingClientRect();
  const overlaps =
    card.left < panel.right &&
    card.right > panel.left &&
    card.top < panel.bottom &&
    card.bottom > panel.top;

  if (overlaps) tracker.setAttribute(COVERED_ATTR, '');
  else tracker.removeAttribute(COVERED_ATTR);
}

export function releaseRoom(): void {
  document.querySelectorAll<HTMLElement>(`[${ROOM_ATTR}]`).forEach((el) => {
    el.removeAttribute(ROOM_ATTR);
  });
  document.querySelectorAll<HTMLElement>(`[${COVERED_ATTR}]`).forEach((el) => {
    el.removeAttribute(COVERED_ATTR);
  });
  document.querySelectorAll<HTMLElement>(`[${TAIL_ATTR}]`).forEach((el) => {
    el.removeAttribute(TAIL_ATTR);
    el.style.removeProperty(TAIL_VAR);
  });
}
